import { useState, useEffect } from 'react';
import { db } from "../../firebase";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { useNavigate, useParams } from "react-router";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import * as theme from '../../theme';
import dayjs from 'dayjs';
import StateSelect from '../../components/StateSelect';
import Button from "@mui/material/Button";
import ErrorDisplay from '../../components/ErrorDisplay';
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

export default function EditClient() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    dob: '',
    street: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    concerns: '',
    immediateFamily: false,
  });
  const [originalData, setOriginalData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    dob: '',
    street: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    concerns: '',
    immediateFamily: false,
  });
  const [practitionerId, setPractitionerId] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { id } = useParams();

  const clearError = () => {
    setError('');
  };

  useEffect(() => {
    if (id != null) {
      getClient();
    }
  }, [id]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDateChange = (value) => {
    setFormData({ ...formData, dob: value });
  };

  const handleStateChange = (e) => {
    setFormData({ ...formData, state: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const clientData = {
        ...formData,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.toLowerCase().trim(),
        dob: formData.dob ? formData.dob.format('YYYY-MM-DD') : null,
        dateModified: serverTimestamp(),
        street: formData.street.trim(),
        address2: formData.address2.trim() || null,
        city: formData.city.trim(),
        zip: formData.zip.trim(),
        immediateFamily: formData.immediateFamily,
      };

      // Update the client document
      const clientRef = doc(db, "clients", id);
      await updateDoc(clientRef, clientData);

      // Check if firstName, lastName, or email changed
      const fieldsToCheck = ['firstName', 'lastName', 'email'];
      const shouldUpdatePractitioner = fieldsToCheck.some(field => 
        formData[field] !== originalData[field]
      );

      // Check if firstName or lastName changed for clientMap update
      const shouldUpdateClientMap = ['firstName', 'lastName'].some(field => 
        formData[field] !== originalData[field]
      );

      if (shouldUpdatePractitioner) {
        const listRef = doc(db, "practitioners", practitionerId, "clients", "list");
        
        // Get the current list document
        const listSnap = await getDoc(listRef);
        if (listSnap.exists()) {
          const listData = listSnap.data();
          const updatedClients = listData.clients.map(client => {
            if (client.id === id) {
              return {
                ...client,
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                email: formData.email.toLowerCase().trim()
              };
            }
            return client;
          });
  
          // Update the list document with the modified clients array
          await updateDoc(listRef, {
            clients: updatedClients
          });
        }
      }

      if (shouldUpdateClientMap) {
        const clientMapRef = doc(db, "app", "clientMap");
        const clientMapSnap = await getDoc(clientMapRef);
        if (clientMapSnap.exists()) {
          const clientMapData = clientMapSnap.data();
          const updatedClients = clientMapData.clients.map(client => {
            if (client.id === id) {
              return {
                ...client,
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim()
              };
            }
            return client;
          });

          // Update the clientMap document with the modified clients array
          await updateDoc(clientMapRef, {
            clients: updatedClients
          });
        }
      }

      navigate(`/clients/${id}`);
    } catch (err) {
      setError("Failed to edit client: " + err.message);
    }

    setLoading(false);
  };

  async function getClient() {
    try {
      const clientRef = doc(db, "clients", id);
      const docSnap = await getDoc(clientRef);
      const clientData = docSnap.data();
      setFormData({
        ...clientData,
        address2: clientData.address2 || '', // Ensure address2 is set, default to empty string
        dob: clientData.dob != null ? dayjs(clientData.dob) : null,
        immediateFamily: clientData.immediateFamily || false,
      });
      setOriginalData({
        ...clientData,
        address2: clientData.address2 || '', // Ensure address2 is set, default to empty string
        dob: clientData.dob != null ? dayjs(clientData.dob) : null,
        immediateFamily: clientData.immediateFamily || false,
      });
      setPractitionerId(clientData.practitionerId);
    } catch (err) {
      console.log("Failed get client: " + err.message);
    }
  }

  return (
    <div style={theme.Container.basic}>
      <div style={theme.Heading}>Edit Client</div>
      <div style={theme.SubHeadingText}>
        Fill out the form below to edit the client's information.
      </div>
      <div style={{ marginTop: 15, marginBottom: 30 }}>
        <div style={{ marginTop: 0, marginBottom: 30 }}>
          <ErrorDisplay
            message={error}
            type={'error'}
            clearMessage={clearError}
          />
        </div>        
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ ...theme.FullForm.form, gap: 4 }}
        >
          <TextField
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            variant="filled"
            required
          />
          <TextField
            label="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            variant="filled"
            required
          />
          <TextField
            label="Email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            variant="filled"
            required
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MobileDatePicker 
              label="Date of Birth"
              value={formData.dob != '' ? formData.dob : null}
              onChange={handleDateChange} 
              maxDate={dayjs()}
              openTo="year"
              views={['year', 'month', 'day']}
              yearsOrder="desc"
              slotProps={{
                textField: {
                  variant: "filled"
                },
              }}
            />
          </LocalizationProvider>
          <TextField
            label="Mailing Address"
            name="street"
            value={formData.street}
            onChange={handleChange}
            variant="filled"
            required
          />
          <TextField
            label="Address Line 2"
            name="address2"
            value={formData.address2}
            onChange={handleChange}
            variant="filled"
          />
          <TextField
            label="City"
            name="city"
            value={formData.city}
            onChange={handleChange}
            variant="filled"
            required
          />
          <Box sx={{ display: "flex", width: '100%', gap: 2 }}>
            <StateSelect 
              value={formData.state} 
              onChange={handleStateChange}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Zip Code"
              name="zip"
              value={formData.zip}
              onChange={handleChange}
              variant="filled"
              required
              sx={{ flex: 1 }}
            />
          </Box>
          <TextField 
            label="Specific Concerns/Diagnosis"
            name="concerns"
            value={formData.concerns}
            onChange={handleChange}
            variant="filled"
            multiline
            rows={4} 
            fullWidth
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.immediateFamily}
                onChange={(e) => setFormData({ ...formData, immediateFamily: e.target.checked })}
              />
            }
            label={
              <div><strong>Immediate Family Member of Practitioner?</strong><br />Qualifies for Practitioner Scan Rate (ex. Spouse, Children, Pets)</div>
            }
          />
          <Box sx={{ display: "flex", width: '100%', gap: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              sx={{ flex: 1 }}
              onClick={() => navigate(`/clients/${id}`)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ flex: 3 }}
            >
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </Box>
        </Box>
      </div>
    </div>
  );
}